// src/components/MedicalReportManager.js
import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Box, Button, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Alert, TextField, Menu, MenuItem } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import BugReportIcon from '@mui/icons-material/BugReport';
import SendIcon from '@mui/icons-material/Send';
import OCRComponent from './OCRComponent';
import PDFProcessor from './PDFProcessor';
import Disclaimer from './Disclaimer';
import { API_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import ConsentModal from './ConsentModal';
import useRatingPrompt from '../hooks/useRatingPrompt';
import RatingModal from './RatingModal';

// PNG senza trasparenza: il decoder WebP di jsPDF ignora il canale alpha e mostra un alone grigio
const logo = process.env.PUBLIC_URL + '/logo-pdf.png';
const PricingModal = lazy(() => import('./PricingModal'));
const MarkdownRenderer = lazy(() => import('./MarkdownRenderer'));

// Funzione di utilità per leggere i cookie (usata per migrazione legacy)
const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for(let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const MedicalReportManager = () => {
  // ---- Subscription (replaces cookie-based usage) ----
  const {
    tier,
    usageCount,
    usageRemaining,
    usageLimitReached,
    usagePct,
    incrementUsage,
    activatePro,
    activateTeam,
    joinTeam,
    generateTeamCode,
    sub,
  } = useSubscription();

  const navigate = useNavigate();
  const [showPricingModal, setShowPricingModal] = useState(false);

  // Listen for header badge click → open pricing modal
  useEffect(() => {
    const handler = () => setShowPricingModal(true);
    window.addEventListener('trm:open-pricing', handler);
    return () => window.removeEventListener('trm:open-pricing', handler);
  }, []);

  // ---- Stati generali ----
  const [mode, setMode] = useState(null);
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const pendingFileActionRef = React.useRef(null);
  const [showBanner, setShowBanner] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [multipleImages, setMultipleImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [allImageTexts, setAllImageTexts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [extractedText, setExtractedText] = useState('');
  const [ocrInProgress, setOcrInProgress] = useState(false);
  const [isTextFromDigitalPDF, setIsTextFromDigitalPDF] = useState(false);
  const { isOpen: ratingOpen, dismiss: dismissRating, submit: submitRating, openManually: openRating } = useRatingPrompt(apiResponse, tier.key);

  // Stati per il modal di segnalazione problemi
  const [showReportModal, setShowReportModal] = useState(false);
  const [privacyMenuAnchor, setPrivacyMenuAnchor] = useState(null);
  const [reportText, setReportText] = useState('');
  const [reportEmail, setReportEmail] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  // Stati per rotazione
  const [rotationAngle, setRotationAngle] = useState(0);
  const [isImageTransformed, setIsImageTransformed] = useState(false);
  const [originalImageData, setOriginalImageData] = useState(null);
  const [transformedImageData, setTransformedImageData] = useState(null);

  // Ref per tracciare se l'OCR è stato avviato
  const ocrStarted = useRef(false);
  const ocrTimerRef = useRef(null);

  const fileInputRef = useRef(null);


  // Migrate from old cookie-based consent to localStorage (one-time)
  useEffect(() => {
    const oldCookie = getCookie('termsAccepted');
    if (oldCookie === 'true') {
      // Clear old cookie — user must re-accept with granular consent
      document.cookie = 'termsAccepted=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
    // Check if existing consent has expired (>2 years)
    const ts = localStorage.getItem('trmConsentTimestamp');
    if (ts) {
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      if (new Date(ts) < twoYearsAgo) {
        localStorage.removeItem('trmUserHash');
        localStorage.removeItem('trmConsentTimestamp');
      }
    }
  }, []);
  
  // Reset degli stati quando cambia l'immagine
  useEffect(() => {
    // Pulisci il timer quando l'immagine cambia
    if (ocrTimerRef.current) {
      clearTimeout(ocrTimerRef.current);
      ocrTimerRef.current = null;
    }
  }, [imageData]);
  
  // Cleanup del timer quando il componente viene smontato
  useEffect(() => {
    return () => {
      if (ocrTimerRef.current) {
        clearTimeout(ocrTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (imageData && !ocrStarted.current && !ocrInProgress) {
      // Pulisci eventuali timer precedenti
      if (ocrTimerRef.current) {
        clearTimeout(ocrTimerRef.current);
      }
      
      // Imposta un nuovo timer per avviare OCR dopo 8 secondi
      ocrTimerRef.current = setTimeout(() => {
        if (!ocrStarted.current) {
          console.log("Avvio automatico OCR dopo 8 secondi");
          ocrStarted.current = true;
          setOcrInProgress(true);
        }
      }, 8000); // 8 secondi
    }
    
    return () => {
      // Pulisci il timer quando il componente viene smontato o imageData cambia
      if (ocrTimerRef.current) {
        clearTimeout(ocrTimerRef.current);
      }
    };
  }, [imageData, ocrInProgress]);
  
  // incrementUsage viene dal hook useSubscription (localStorage + SW sync)

  const hasValidConsent = () => !!localStorage.getItem('trmUserHash');

  const requireConsent = (action) => {
    if (hasValidConsent()) {
      action();
    } else {
      pendingFileActionRef.current = action;
      setConsentModalOpen(true);
    }
  };

  // Funzione per aprire il modal di segnalazione
  const handleOpenReportModal = () => {
    setShowReportModal(true);
  };

  useEffect(() => {
    window.addEventListener('open-report-modal', handleOpenReportModal);
    return () => window.removeEventListener('open-report-modal', handleOpenReportModal);
  }, []);

  // Funzione per chiudere il modal di segnalazione
  const handleCloseReportModal = () => {
    setShowReportModal(false);
    setReportText('');
    setReportEmail('');
  };

  // Funzione per inviare la segnalazione
  const handleSubmitReport = async () => {
    if (!reportText.trim()) {
      alert('Inserisci una descrizione del problema');
      return;
    }

    setIsSubmittingReport(true);
    
    const requestData = {
      description: reportText,
      email: reportEmail,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    console.log('Invio segnalazione a:', `${API_URL}/report-issue`);
    console.log('Dati inviati:', requestData);
    
    try {
      const response = await fetch(`${API_URL}/report-issue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      console.log('Risposta ricevuta:', response.status, response.statusText);

      if (response.ok) {
        const result = await response.json();
        console.log('Risposta JSON:', result);
        alert('Segnalazione inviata con successo! Grazie per il tuo feedback.');
        handleCloseReportModal();
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Errore sconosciuto' }));
        console.error('Errore dal server:', errorData);
        alert(`Errore nell'invio della segnalazione: ${errorData.error || 'Errore sconosciuto'}`);
      }
    } catch (error) {
      console.error('Errore di rete:', error);
      alert('Errore di connessione. Verifica che il server sia in esecuzione e riprova.');
    } finally {
      setIsSubmittingReport(false);
    }
  };

  
const handleFileChange = (e) => {
  const files = Array.from(e.target.files);
  if (files.length === 0) return;
  // Reset input so same file can be re-selected after cancel
  e.target.value = '';

  requireConsent(() => {
    // Se c'è un PDF, prendi solo il primo (i PDF sono sempre singoli)
    const pdfFile = files.find(file => file.type === 'application/pdf');
    if (pdfFile) {
      setPdfFile(pdfFile);
      setImageData(null);
      return;
    }

    // Se ci sono solo immagini, gestiscile
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length > 0) {
      setPdfFile(null);

      if (imageFiles.length === 1) {
        // Una sola immagine - comportamento originale
        const reader = new FileReader();
        reader.onloadend = () => {
          setImageData(reader.result);
          setOriginalImageData(reader.result);
          setRotationAngle(0);
          setIsImageTransformed(false);
          ocrStarted.current = false;

          // Imposta un timer per avviare l'OCR dopo 10 secondi
          ocrTimerRef.current = setTimeout(() => {
            if (!ocrStarted.current) {
              ocrStarted.current = true;
              setOcrInProgress(true);
            }
          }, 10000); // 10 secondi
        };
        reader.readAsDataURL(imageFiles[0]);
      } else {
        // Più immagini - nuovo comportamento multi-pagina
        processMultipleImages(imageFiles);
      }
    }
  });
};





// Funzione per processare più immagini
const processMultipleImages = async (imageFiles) => {
  try {
    const images = [];
    
    for (let i = 0; i < imageFiles.length; i++) {
      const file = imageFiles[i];
      const reader = new FileReader();
      
      await new Promise((resolve) => {
        reader.onloadend = () => {
          images.push({
            index: i,
            name: file.name,
            data: reader.result
          });
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }
    
    setMultipleImages(images);
    setCurrentImageIndex(0);
    setAllImageTexts(new Array(images.length).fill(''));
    setImageData(images[0].data); // Mostra la prima immagine
    setOriginalImageData(images[0].data);
    setRotationAngle(0);
    setIsImageTransformed(false);
    ocrStarted.current = false;
    
    // Imposta un timer per avviare l'OCR dopo 10 secondi
    ocrTimerRef.current = setTimeout(() => {
      if (!ocrStarted.current) {
        ocrStarted.current = true;
        setOcrInProgress(true);
      }
    }, 10000); // 10 secondi
    
  } catch (error) {
    console.error('Errore nel processing delle immagini multiple:', error);
  }
};

// Funzione per ruotare l'immagine
const rotateImage = (direction) => {
  if (!imageData) return;
  
  // Calcola il nuovo angolo di rotazione (±90 gradi alla volta)
  const newAngle = direction === 'left' 
    ? (rotationAngle - 90) % 360 
    : (rotationAngle + 90) % 360;
  
  // Assicurati che l'angolo sia positivo
  const normalizedAngle = (newAngle + 360) % 360;
  
  console.log(`Rotazione da ${rotationAngle}° a ${normalizedAngle}°`);
  
  // Aggiorna l'angolo di rotazione
  setRotationAngle(normalizedAngle);
  
  // Imposta il flag per indicare che l'immagine è stata trasformata
  setIsImageTransformed(true);
  
  // Se stiamo tornando all'angolo 0, ripristiniamo l'immagine originale
  if (normalizedAngle === 0) {
    setImageData(originalImageData);
    setTransformedImageData(originalImageData);
    setIsImageTransformed(false);
  } else if (originalImageData) {
    // Altrimenti, usiamo l'immagine originale con la rotazione CSS
    setImageData(originalImageData);
  }
  
  // Reset dello stato OCR
  setExtractedText('');
  ocrStarted.current = false;
  
  // Reset del timer OCR
  if (ocrTimerRef.current) {
    clearTimeout(ocrTimerRef.current);
  }
  
  // Imposta un nuovo timer per l'OCR
  ocrTimerRef.current = setTimeout(() => {
    if (!ocrStarted.current) {
      ocrStarted.current = true;
      setOcrInProgress(true);
    }
  }, 10000);
};

// Funzione per ripristinare l'immagine originale
const resetImageTransformation = () => {
  if (!originalImageData) return;
  
  setImageData(originalImageData);
  setIsImageTransformed(false);
  setExtractedText('');
  ocrStarted.current = false;
  
  // Imposta un nuovo timer per avviare l'OCR dopo 10 secondi
  if (ocrTimerRef.current) {
    clearTimeout(ocrTimerRef.current);
  }
  
  ocrTimerRef.current = setTimeout(() => {
    if (!ocrStarted.current) {
      ocrStarted.current = true;
      setOcrInProgress(true);
    }
  }, 10000); // 10 secondi
};

// Funzione per avviare manualmente l'OCR
const startOCRManually = () => {
  // Cancella il timer di avvio automatico
  if (ocrTimerRef.current) {
    clearTimeout(ocrTimerRef.current);
    ocrTimerRef.current = null;
  }
  
  // Avvia OCR immediatamente
  if (!ocrStarted.current && !ocrInProgress) {
    console.log("Avvio manuale OCR");
    ocrStarted.current = true;
    setOcrInProgress(true);
  }
};



const handleCancel = () => {
  setMode(null);
  setImageData(null);
  setOriginalImageData(null);
  setTransformedImageData(null);
  setPdfFile(null); // Resetta anche il PDF
  setMultipleImages([]); // Resetta immagini multiple
  setCurrentImageIndex(0); // Resetta indice immagine corrente
  setAllImageTexts([]); // Resetta testi delle immagini
  setApiResponse(null);
  setExtractedText('');
  setOcrInProgress(false);
  setIsTextFromDigitalPDF(false);
  setLoading(false);
  setShowBanner(false);
  setRotationAngle(0);
  setIsImageTransformed(false);
  ocrStarted.current = false;
  
  // Cancella il timer se esiste
  if (ocrTimerRef.current) {
    clearTimeout(ocrTimerRef.current);
    ocrTimerRef.current = null;
  }
};

const handleSubmit = async () => {
  if (!imageData && !pdfFile) return;
  if (ocrInProgress) {
    alert("Attendere il completamento dell'elaborazione prima di inviare il referto.");
    return;
  }
  if (!extractedText || extractedText.trim().length < 10) {
    alert("Il software non ha rilevato un testo corretto. Prova con un'immagine più nitida o un PDF di migliore qualità.");
    return;
  }
  
  // Verifica il limite di utilizzo prima di procedere
  if (usageLimitReached) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'free_limit_reached', trigger: 'submit' });
    setShowPricingModal(true);
    return;
  }
  
  setLoading(true);
  setApiResponse(null);
  
  try {
    const formData = new FormData();
    formData.append('extractedText', extractedText);
    formData.append('consentOCR', 'true');
    formData.append('consentAI', 'true');
    if (sub.token) formData.append('proToken', sub.token);

    // Se abbiamo un'immagine, la aggiungiamo alla richiesta
    if (imageData) {
      // Utilizzare l'immagine trasformata se disponibile, altrimenti quella originale
      const imageToSend = isImageTransformed && transformedImageData ? transformedImageData : imageData;
      const blob = await (await fetch(imageToSend)).blob();
      formData.append('image', blob, 'upload.jpg');
    }
    
    // Se abbiamo un PDF, lo aggiungiamo alla richiesta
    if (pdfFile) {
      formData.append('pdf', pdfFile);
    }

    const uploadHeaders = {};
    if (sub.token) uploadHeaders['X-TRM-Token'] = sub.token;

    const response = await fetch(`${API_URL}/upload`, {
      method: "POST",
      headers: uploadHeaders,
      body: formData
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('data:', data.response.choices[0].message.content);
    
    // Incrementa il contatore di utilizzo nei cookie
    incrementUsage();
    
    setApiResponse(data);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'translation_completed' });
  } catch (error) {
    console.error("Errore nella chiamata API:", error);
    setApiResponse({ error: error.message || "Errore nel caricamento del referto." });
  } finally {
    setLoading(false);
  }
};

// Gestione della selezione della modalità
const handleModeSelect = (selectedMode) => {
  if (usageLimitReached) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'free_limit_reached', trigger: 'mode_select' });
    setShowPricingModal(true);
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'click_carica_referto' });
  setMode(selectedMode);
  setShowBanner(true);
};

// Funzione helper per formattare la traduzione nel PDF
const formatTranslationForPDF = (text, doc, margin, startY, pageHeight, maxWidth) => {
  let yPosition = startY;
  
  // Lista dei titoli principali che devono essere in grassetto
  const mainTitles = ['Sintesi', 'Spiegazione dei termini', 'Domande da fare al medico', 'Indicazioni sullo specialista'];
  
  // Dividi il testo in righe
  const lines = text.split('\n');
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) {
      yPosition += 3; // Spazio per righe vuote
      continue;
    }
    
    // Controlla se è un titolo principale (inizia con **)
    if (line.startsWith('**') && line.endsWith('**')) {
      if (yPosition > pageHeight - 40) {
        doc.addPage();
        yPosition = 20;
      }
      
      const titleText = line.replace(/\*\*/g, '');
      const isMainTitle = mainTitles.some(title => 
        titleText.toLowerCase().includes(title.toLowerCase()) || 
        title.toLowerCase().includes(titleText.toLowerCase())
      );
      
      doc.setFontSize(11);
      doc.setFont(undefined, isMainTitle ? 'bold' : 'normal');
      const title = titleText.toUpperCase();
      doc.text(title, margin, yPosition);
      yPosition += 8;
      
    } else if (line.startsWith('- **')) {
      // Elemento della lista con termine in grassetto
      if (yPosition > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;
      }
      
      const parts = line.split('**:');
      if (parts.length === 2) {
        const term = parts[0].replace(/^-\s*\*\*/, '').trim();
        const definition = parts[1].trim();
        
        // Termine in maiuscolo e normale (non grassetto)
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`${term.toUpperCase()}:`, margin, yPosition);
        yPosition += 6;
        
        // Definizione con trattino
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const definitionLines = doc.splitTextToSize(`- ${definition}`, maxWidth - 10);
        for (const defLine of definitionLines) {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text(defLine, margin + 10, yPosition);
          yPosition += 5;
        }
        yPosition += 4; // Spazio extra tra elementi
      }
    } else {
      // Testo normale
      if (yPosition > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const textLines = doc.splitTextToSize(line, maxWidth);
      for (const textLine of textLines) {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(textLine, margin, yPosition);
        yPosition += 5;
      }
      yPosition += 2; // Spazio tra paragrafi
    }
  }
  
  return { finalY: yPosition };
};

// Funzione per ottenere le dimensioni dell'immagine mantenendo le proporzioni
const getImageDimensions = (img, maxWidth) => {
  const ratio = img.width / img.height;
  let width = maxWidth;
  let height = width / ratio;
  
  // Se l'altezza è eccessiva, riduci le dimensioni mantenendo le proporzioni
  if (height > maxWidth * 0.5) {  // Massimo 50% dell'altezza della pagina
    height = maxWidth * 0.5;
    width = height * ratio;
  }
  
  return { width, height };
};

// Funzione per scaricare la traduzione in PDF
const downloadTranslationPDF = async () => {
  if (!apiResponse || !apiResponse.response || !extractedText) {
    alert("Nessuna traduzione disponibile per il download.");
    return;
  }

  try {
    // Crea un nuovo documento PDF
    const { default: jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    
    // Configurazione del font e dimensioni
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    
    // Carica l'immagine del logo
    const img = new Image();
    img.src = logo;
    
    // Ottieni le dimensioni mantenendo le proporzioni
    const logoDimensions = await new Promise((resolve) => {
      img.onload = () => {
        const dimensions = getImageDimensions(img, maxWidth * 0.4); // Massimo 40% della larghezza disponibile
        resolve(dimensions);
      };
    });
    
    // Funzione per aggiungere l'header con il logo su ogni pagina
    const addHeader = () => {
      try {
        // Posiziona il logo centrato in alto
        const logoX = (pageWidth - logoDimensions.width) / 2;
        
        // Aggiungi il logo mantenendo le proporzioni originali
        doc.addImage(
          logo, 
          'PNG', 
          logoX, 
          15, 
          logoDimensions.width,
          logoDimensions.height,
          undefined,
          'FAST' // senza compressione il PNG gonfia il PDF di ~600KB
        );
        
        // Spazio sotto il logo
        return 15 + logoDimensions.height + 10;
      } catch (error) {
        console.error("Errore nel caricamento del logo:", error);
        return 30; // Se c'è un errore, torna alla posizione di default
      }
    };
    
    // Aggiungi l'header alla prima pagina
    let yPosition = addHeader();
    
    // Data e ora
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const now = new Date();
    const dateString = now.toLocaleDateString('it-IT') + ' ' + now.toLocaleTimeString('it-IT');
    doc.text(`Generato il: ${dateString}`, margin, yPosition);
    yPosition += 10;
    
    // Linea separatrice
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 15;
    
    // Testo originale estratto dall'OCR
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('TESTO ORIGINALE RILEVATO:', margin, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const originalText = extractedText;
    const originalLines = doc.splitTextToSize(originalText, maxWidth);
    
    // Aggiungi il testo originale con controllo overflow pagina
    for (let i = 0; i < originalLines.length; i++) {
      if (yPosition > pageHeight - 50) {
        doc.addPage();
        yPosition = addHeader() + 30; // Aggiungi l'header anche alle pagine successive
      }
      doc.text(originalLines[i], margin, yPosition);
      yPosition += 5;
    }
    
    // Spazio tra sezioni
    yPosition += 10;
    
    // Traduzione
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    if (yPosition > pageHeight - 50) {
      doc.addPage();
      yPosition = addHeader() + 30; // Aggiungi l'header anche alle pagine successive
    }
    doc.text('TRADUZIONE IN ITALIANO:', margin, yPosition);
    yPosition += 10;

    // Stessa etichetta mostrata sopra la risposta nell'interfaccia
    if (apiResponse.aiDisclaimer) {
      doc.setFontSize(9);
      doc.setFont(undefined, 'italic');
      doc.text('Spiegazione generata da AI', margin, yPosition);
      yPosition += 8;
    }

    // Processa la traduzione con formattazione migliorata
    const translationText = apiResponse.response.choices[0].message.content;
    const formattedTranslation = formatTranslationForPDF(translationText, doc, margin, yPosition, pageHeight, maxWidth);
    yPosition = formattedTranslation.finalY;
    
    // Aggiungi il footer con il link cliccabile su ogni pagina
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Aggiungi il link cliccabile in basso a sinistra
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 255); // Blu per il link
      doc.textWithLink('Visita il nostro sito web', margin, pageHeight - 15, {
        url: 'https://traduttorerefertimedici.it',
        underline: true
      });
      
      // Aggiungi il disclaimer in basso al centro
      doc.setTextColor(0, 0, 0); // Torna al nero
      doc.setFont(undefined, 'italic');
      doc.text('Traduttore Referti Medici - Le informazioni sono puramente informative e non sostituiscono una consulenza medica.', 
               pageWidth / 2, pageHeight - 10, { align: 'center' });
    }
    
    // Aggiungi la pagina con l'informativa GDPR
    doc.addPage();
    let gdprY = margin + 10;

    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('INFORMATIVA PRIVACY \u2014 ART. 13 GDPR (UE) 2016/679', margin, gdprY);
    gdprY += 12;

    doc.setFontSize(9);
    doc.setFont(undefined, 'italic');

    const gdprLines = [
      'Questo documento \u00e8 stato generato da Traduttore Referti Medici.',
      'Le informazioni contenute sono di natura medica e devono essere trattate',
      'come dati personali sensibili ai sensi del GDPR.',
      '',
      '\u2022 Il documento non deve essere condiviso con terzi non autorizzati.',
      '\u2022 I dati del paziente devono essere protetti secondo le normative vigenti.',
      '\u2022 Questo documento \u00e8 generato a scopo informativo e non sostituisce',
      '  una consulenza medica professionale.',
      '',
      'Contatto: info@traduttorerefertimedici.it',
    ];

    for (const line of gdprLines) {
      if (gdprY > pageHeight - margin) {
        doc.addPage();
        gdprY = margin + 10;
      }
      doc.text(line, margin, gdprY);
      gdprY += 5.5;
    }
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);

    // Aggiungi il footer GDPR alla pagina disclaimer
    const gdprPageNum = doc.internal.getNumberOfPages();
    doc.setPage(gdprPageNum);
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 255);
    doc.textWithLink('Visita il nostro sito web', margin, pageHeight - 15, {
      url: 'https://traduttorerefertimedici.it',
      underline: true
    });
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'italic');
    doc.text('Traduttore Referti Medici - Le informazioni sono puramente informative e non sostituiscono una consulenza medica.',
             pageWidth / 2, pageHeight - 10, { align: 'center' });

    // Scarica il PDF
    const fileName = `traduzione_referto_${now.toISOString().slice(0, 10)}.pdf`;
    doc.save(fileName);
    
  } catch (error) {
    console.error("Errore nella generazione del PDF:", error);
    alert("Errore nella generazione del PDF. Riprova.");
  }
};


// Componente utilizzo — enterprise dark mode con barra progresso
const UsageInfoBanner = () => {
  const isUnlimited = tier.monthlyLimit === Infinity;

  if (isUnlimited) {
    return (
      <div className="usage-block">
        <div className="usage-block__header">
          <span className="usage-block__label">Traduzioni</span>
          <span className="usage-block__count" style={{ color: 'var(--e-team)' }}>
            {usageCount} <span>· illimitate</span>
          </span>
        </div>
        <div className="usage-bar">
          <div className="usage-bar__fill" style={{ width: '100%', opacity: 0.3 }} />
        </div>
      </div>
    );
  }

  if (usageLimitReached) {
    return (
      <>
        <Alert severity="error" icon={<WarningIcon />} sx={{ mt: 1, mb: 1 }}>
          Limite di {tier.monthlyLimit} traduzioni {tier.windowLabel || 'mensili'} raggiunto.{' '}
          <button
            onClick={() => { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'click_passa_a_pro' }); setShowPricingModal(true); }}
            style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontWeight: 700 }}
          >
            Passa a Pro per continuare →
          </button>
        </Alert>
        <div className="usage-block">
          <div className="usage-block__header">
            <span className="usage-block__label">Utilizzo {tier.windowLabel || 'mensile'}</span>
            <span className="usage-block__count">{usageCount} <span>/ {tier.monthlyLimit}</span></span>
          </div>
          <div className="usage-bar">
            <div className="usage-bar__fill usage-bar__fill--danger" style={{ width: '100%' }} />
          </div>
        </div>
      </>
    );
  }

  if (usageRemaining) {
    return (
      <div className="usage-block">
        <div className="usage-block__header">
          <span className="usage-block__label">Utilizzo mensile</span>
          <span className="usage-block__count">{usageCount} <span>/ {tier.monthlyLimit}</span></span>
        </div>
        <div className="usage-bar">
          <div className="usage-bar__fill usage-bar__fill--warn" style={{ width: `${usagePct}%` }} />
        </div>
        <div className="usage-block__upgrade">
          Rimangono {usageRemaining} {usageRemaining === 1 ? 'traduzione' : 'traduzioni'} questo mese.{' '}
          <button onClick={() => { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'click_passa_a_pro' }); setShowPricingModal(true); }}>Passa a Pro →</button>
        </div>
      </div>
    );
  }

  return (
    <div className="usage-block">
      <div className="usage-block__header">
        <span className="usage-block__label">Utilizzo mensile</span>
        <span className="usage-block__count">{usageCount} <span>/ {tier.monthlyLimit}</span></span>
      </div>
      <div className="usage-bar">
        <div className="usage-bar__fill" style={{ width: `${usagePct}%` }} />
      </div>
    </div>
  );
};


return (
  <Box sx={{ textAlign: 'center', p: 2 }}>

    {/* Pricing Modal — caricato lazy solo quando aperto */}
    <Suspense fallback={null}>
      <PricingModal
        open={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        currentTier={tier.key}
        teamCode={sub.teamCode}
        onUpgrade={(newTier) => {
          if (newTier === 'pro') activatePro();
          if (newTier === 'team') activateTeam();
          setShowPricingModal(false);
        }}
        onGenerateCode={generateTeamCode}
        onJoinTeam={joinTeam}
        proToken={sub.token}
      />
    </Suspense>

    {/* GDPR Consent Modal */}
    <ConsentModal
      open={consentModalOpen}
      onAccepted={() => {
        setConsentModalOpen(false);
        if (pendingFileActionRef.current) {
          const action = pendingFileActionRef.current;
          pendingFileActionRef.current = null;
          action();
        }
      }}
      onCancel={() => {
        setConsentModalOpen(false);
        pendingFileActionRef.current = null;
        if (fileInputRef.current) fileInputRef.current.value = '';
      }}
    />

    {showBanner && (
      <Box
        sx={{
          backgroundColor: '#ffffff',
          color: 'primary.main',
          p: 1,
          mb: 2,
          borderRadius: 1,
          boxShadow: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          border: '1px solid',
          borderColor: 'secondary.main',
          '@keyframes blinker': {
            '50%': { opacity: 0 }
          },
          animation: 'blinker 1s linear infinite'
        }}
      >
        <Typography variant="body2">
          <strong>ATTENZIONE!!!</strong> Assicurati che l'immagine non contenga dati personali (es. Nome, Cognome, etc) per tutelare la tua privacy.
        </Typography>
        <Button
          onClick={() => setShowBanner(false)}
          sx={{
            minWidth: 'auto',
            p: 0.5,
            color: 'secondary.main'
          }}
        >
          ✕
        </Button>
      </Box>
    )}

    {/* Elaborazione immagine con OCR */}
    {imageData && ocrStarted.current && (
      <OCRComponent 
        key={`ocr-${currentImageIndex}-${multipleImages.length}`} // Forza re-render per ogni immagine
        image={imageData} 
        rotationAngle={rotationAngle}
        onExtracted={(text) => {
          console.log(`OCR completato per immagine ${currentImageIndex + 1}/${multipleImages.length}:`, text.length, 'caratteri');
          
          if (multipleImages.length > 1) {
            // Per immagini multiple, salva il testo della pagina corrente
            const newTexts = [...allImageTexts];
            newTexts[currentImageIndex] = text;
            setAllImageTexts(newTexts);
            
            // Se non è l'ultima immagine, passa alla prossima
            if (currentImageIndex < multipleImages.length - 1) {
              console.log(`Passaggio all'immagine ${currentImageIndex + 2}/${multipleImages.length}`);
              
              const nextIndex = currentImageIndex + 1;
              setCurrentImageIndex(nextIndex);
              setImageData(multipleImages[nextIndex].data);
              setOriginalImageData(multipleImages[nextIndex].data);
              setRotationAngle(0);
              setIsImageTransformed(false);
              ocrStarted.current = false;
              setOcrInProgress(false);
              
              // Avvia OCR sulla prossima immagine dopo un breve ritardo
              setTimeout(() => {
                console.log(`Avvio OCR per immagine ${nextIndex + 1}/${multipleImages.length}`);
                ocrStarted.current = true;
                setOcrInProgress(true);
              }, 1500); // Aumentato il ritardo per dare tempo al re-render
            } else {
              // Ultima immagine completata, combina tutti i testi
              console.log('Combinazione testi di tutte le immagini');
              const combinedText = newTexts.join('\n\n--- IMMAGINE SUCCESSIVA ---\n\n');
              setExtractedText(combinedText);
              setOcrInProgress(false);
            }
          } else {
            // Per immagine singola, comportamento originale
            setExtractedText(text);
            setOcrInProgress(false);
          }
        }}
        onStart={() => {
          console.log(`Avvio OCR per immagine ${currentImageIndex + 1}/${multipleImages.length}`);
          setOcrInProgress(true);
        }}
      />
    )}

    {/* Elaborazione PDF */}
    {pdfFile && (
      <PDFProcessor 
        file={pdfFile} 
        onTextExtracted={(text, isFromDigitalPDF = false) => {
          console.log("Testo estratto dal PDF:", text);
          setExtractedText(text);
          setIsTextFromDigitalPDF(isFromDigitalPDF);
          setOcrInProgress(false);
        }}
        onStart={() => setOcrInProgress(true)}
        onSwitchToCamera={() => {
          // Switch alla modalità fotocamera
          setPdfFile(null);
          setImageData(null);
          setExtractedText('');
          setMode('capture');
        }}
      />
    )}


{mode === null && (
  <Box>
    <Typography variant="h6" sx={{ mb: 3 }}>
    Carica Il tuo referto medico <br /> in formato PDF o immagine <br /> per tradurlo in un italiano <br />  facilmente comprensibile
    </Typography>

    {/* Informazioni sull'utilizzo */}
    <UsageInfoBanner />

    <Button
      variant="outlined"
      onClick={() => handleModeSelect('upload')}
      disabled={usageLimitReached}
      sx={{ mt: 3 }}
    >
      Carica il Referto
    </Button>
    <Box sx={{ mt: 0 }}>
      <Disclaimer />
    </Box>
    {/* Privacy management */}
    <Box sx={{ textAlign: 'center', mt: 1 }}>
      <Button
        size="small"
        variant="text"
        color="inherit"
        sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
        onClick={(e) => setPrivacyMenuAnchor(e.currentTarget)}
      >
        Privacy & Consensi ▾
      </Button>
      <Menu
        anchorEl={privacyMenuAnchor}
        open={Boolean(privacyMenuAnchor)}
        onClose={() => setPrivacyMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MenuItem
          sx={{ fontSize: '0.85rem' }}
          onClick={() => { setPrivacyMenuAnchor(null); navigate('/privacy-policy'); }}
        >
          Informativa Privacy
        </MenuItem>
        <MenuItem
          sx={{ fontSize: '0.85rem', color: 'error.main' }}
          onClick={() => {
            setPrivacyMenuAnchor(null);
            const userHash = localStorage.getItem('trmUserHash');
            if (!userHash) { alert('Nessun consenso attivo trovato.'); return; }
            if (window.confirm('Vuoi revocare il consenso al trattamento dei dati? Non potrai caricare referti finché non accetti nuovamente.')) {
              fetch(`${API_URL}/revoke-terms/${userHash}`, { method: 'DELETE' })
                .then(() => {
                  localStorage.removeItem('trmUserHash');
                  localStorage.removeItem('trmConsentTimestamp');
                  alert('Consensi revocati. Diritti GDPR Art.7(3) esercitati.');
                })
                .catch(() => alert('Errore durante la revoca. Riprova.'));
            }
          }}
        >
          Revoca Consensi
        </MenuItem>
      </Menu>
    </Box>
  </Box>
)}

{/* Modalità upload per file e PDF */}
{mode === 'upload' && (
  <Box>
    <Typography variant="h6" sx={{ mb: 3 }}>
    Carica o fotografa <br /> il tuo referto medico
    </Typography>

    {/* Informazioni sull'utilizzo */}
    <UsageInfoBanner />

    <input
      type="file"
      accept="image/*,application/pdf"
      onChange={handleFileChange}
      ref={fileInputRef}
      multiple
      style={{ display: 'none' }}
    />
{!imageData && !pdfFile ? (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    {/* Box dei pulsanti */}
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        mt: 3
      }}
    >
      <Button
        variant="contained"
        color="primary"
        onClick={() => { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'click_seleziona_file' }); fileInputRef.current.click(); }}
        startIcon={<CloudUploadIcon />}
        disabled={usageLimitReached}
      >
        Seleziona File
      </Button>

      <Button variant="text" onClick={handleCancel}>
        Indietro
      </Button>
    </Box>
    
    {/* Nuova scritta sotto i pulsanti */}
    <Box sx={{ mt: 10 }}>
      <Typography         variant="h7"      >
        Al momento, il software NON è in grado di tradurre Analisi del Sangue.<br />
        Caricare solo referti testuali.      
      </Typography>
    </Box>
  </Box>
) : (
  // resto del codice...
      <Box>
        {/* Visualizza anteprima dell'immagine se disponibile */}
        {imageData && (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    {/* Indicatore per immagini multiple */}
    {multipleImages.length > 1 && (
      <Typography variant="body2" sx={{ mb: 2 }}>
        Immagine {currentImageIndex + 1} di {multipleImages.length}
      </Typography>
    )}
    
    <Box sx={{ 
      position: 'relative', 
      maxWidth: '100%',
      display: 'inline-block',
      transformOrigin: 'center center',
      padding: '20px',
      width: rotationAngle % 180 === 0 ? 'auto' : '100%',
      maxHeight: '60vh',
      overflow: 'hidden',
      textAlign: 'center'
    }}>
      <img
        src={imageData}
        alt="Anteprima"
        style={{
          maxWidth: '100%',
          maxHeight: '60vh',
          borderRadius: 8,
          transform: `rotate(${rotationAngle}deg)`,
          objectFit: 'contain',
          transition: 'transform 0.3s ease-in-out',
          width: rotationAngle % 180 === 0 ? '100%' : 'auto',
          height: rotationAngle % 180 === 0 ? 'auto' : '100%'
        }}
      />
    </Box>
    
    {/* Pulsanti di navigazione per immagini multiple */}
    {multipleImages.length > 1 && !ocrStarted.current && !ocrInProgress && (
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={currentImageIndex === 0}
          onClick={() => {
            const newIndex = currentImageIndex - 1;
            setCurrentImageIndex(newIndex);
            setImageData(multipleImages[newIndex].data);
            setOriginalImageData(multipleImages[newIndex].data);
            setRotationAngle(0);
            setIsImageTransformed(false);
            setExtractedText('');
            ocrStarted.current = false;
          }}
        >
          Immagine Precedente
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={currentImageIndex === multipleImages.length - 1}
          onClick={() => {
            const newIndex = currentImageIndex + 1;
            setCurrentImageIndex(newIndex);
            setImageData(multipleImages[newIndex].data);
            setOriginalImageData(multipleImages[newIndex].data);
            setRotationAngle(0);
            setIsImageTransformed(false);
            setExtractedText('');
            ocrStarted.current = false;
          }}
        >
          Immagine Successiva
        </Button>
      </Box>
    )}
            
            
            {/* Controlli di rotazione per l'immagine - solo visibili prima dell'OCR */}
            {!ocrStarted.current && !ocrInProgress && !loading && (
              <Box sx={{ display: 'flex', mt: 2, gap: 1 }}>
                <IconButton onClick={() => rotateImage('left')} color="primary">
                  <RotateLeftIcon />
                </IconButton>
                <IconButton onClick={() => rotateImage('right')} color="primary">
                  <RotateRightIcon />
                </IconButton>
                {isImageTransformed && (
                  <IconButton onClick={resetImageTransformation} color="primary">
                    <RefreshIcon />
                  </IconButton>
                )}
              </Box>
            )}

            {/* Messaggio informativo sul riconoscimento testo */}
            {!ocrStarted.current && !ocrInProgress && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Se l'immagine ha bisogno di essere ruotata, fallo ora. Il rilevamento del testo inizierà tra poco...
              </Typography>
            )}
            
            {/* Pulsante per avviare manualmente l'OCR */}
            {!ocrStarted.current && !ocrInProgress && (
              <Button
                variant="outlined"
                color="primary"
                onClick={startOCRManually}
                sx={{ mt: 2 }}
              >
                Avvia rilevamento testo
              </Button>
            )}
          </Box>
        )}
        
        {/* Mostra l'informazione sul PDF se caricato */}
        {pdfFile && (
          <Box sx={{ my: 2, p: 2, border: '1px solid', borderColor: 'primary.light', borderRadius: 1 }}>
            <Typography variant="body1">
              <strong>PDF caricato:</strong> {pdfFile.name}
            </Typography>
          </Box>
        )}

        {/* Verifica congruenza per immagini e PDF scansionati (NON per PDF digitali) */}
        {extractedText && !isTextFromDigitalPDF && (
          <Box>
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              <strong>Verifica che non ci siano troppe incongruenze tra il testo rilevato ed il testo del referto medico per ricevere un risultato più accurato.</strong>
              <br />
              <strong>Testo rilevato:</strong> {extractedText}
            </Typography>
          </Box>
        )}
        

{ocrInProgress ? (
  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <CircularProgress size={24} />
    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
      {multipleImages.length > 1 
        ? `Sto leggendo l'immagine ${currentImageIndex + 1} di ${multipleImages.length}...`
        : 'Sto leggendo il tuo referto...'
      }
    </Typography>
  </Box>
) : (
  <>
    {loading ? (
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Attendi qualche secondo, sto traducendo il tuo referto...
        </Typography>
      </Box>
    ) : (
      <>
        {apiResponse ? (
          <Box sx={{ mt: 1 }}>
            {/* Pulsante rimosso per evitare duplicazione */}
          </Box>
        ) : (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              startIcon={<CloudUploadIcon />}
              disabled={!extractedText || extractedText.trim().length < 10 || ocrInProgress || usageLimitReached}
            >
              Traduci
            </Button>
            <Button variant="text" onClick={handleCancel} sx={{ ml: 1 }}>
              Indietro
            </Button>
          </Box>
        )}
      </>
    )}
  </>
)}
</Box>
)}
</Box>
)}



      {/* Visualizzazione della risposta */}
      {apiResponse && apiResponse.response ? (
        <Box>
          {/* aiDisclaimer arriva solo con le risposte di OpenAI, non con i messaggi fissi del server */}
          {apiResponse.aiDisclaimer && (
            <Typography variant="caption" component="p" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
              Spiegazione generata da AI
            </Typography>
          )}
          <Suspense fallback={<CircularProgress size={24} />}>
            <MarkdownRenderer>
              {apiResponse.response.choices[0].message.content}
            </MarkdownRenderer>
          </Suspense>
          
          {/* Pulsanti di azione dopo la traduzione */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3, mb: 3}}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCancel}
            >
              Torna alla Home
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={openRating}
              startIcon={<span>⭐</span>}
            >
              Valuta il servizio
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={downloadTranslationPDF}
            >
              Scarica <br/> la Traduzione in PDF
            </Button>
          </Box>
          
          {/* Mostra le informazioni di utilizzo dopo la traduzione */}
          <UsageInfoBanner />
        </Box>
      ) : apiResponse && apiResponse.error ? (
        <Box>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {apiResponse.error}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCancel}
            >
              Torna alla Home
            </Button>
          </Box>
        </Box>
      ) : null}

      {/* Modal per segnalare problemi */}
      <Dialog open={showReportModal} onClose={handleCloseReportModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BugReportIcon color="primary" />
            Segnala un Problema
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Aiutaci a migliorare il servizio segnalando eventuali malfunzionamenti o problemi riscontrati.
          </Typography>
          
          <TextField
            autoFocus
            margin="dense"
            label="Email (opzionale)"
            type="email"
            fullWidth
            variant="outlined"
            value={reportEmail}
            onChange={(e) => setReportEmail(e.target.value)}
            placeholder="La tua email per eventuali chiarimenti"
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Descrizione del problema"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={reportText}
            onChange={(e) => setReportText(e.target.value)}
            placeholder="Descrivi il problema riscontrato, cosa stavi facendo quando è successo, e qualsiasi altro dettaglio utile..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReportModal} disabled={isSubmittingReport}>
            Annulla
          </Button>
          <Button 
            onClick={handleSubmitReport} 
            variant="contained" 
            startIcon={<SendIcon />}
            disabled={isSubmittingReport || !reportText.trim()}
          >
            {isSubmittingReport ? 'Invio...' : 'Invia Segnalazione'}
          </Button>
        </DialogActions>
      </Dialog>
      <RatingModal
        open={ratingOpen}
        onDismiss={dismissRating}
        onSubmit={submitRating}
      />
    </Box>
  );
};

export default MedicalReportManager;
